import { Box, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Left, Right } from '@urbaninfrastructure/react-icons';
import { FC, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import { H2 } from '../../components';
import CarouselItem from './CarouselItem';

export interface CarouselItemProps {
  carouselItemImage?: { url: string | null } | null;
  carouselItemHeader?: string | null;
  carouselItemDescriptionText?: string | null;
}

interface CarouselProps {
  carouselHeader?: string | null;
  carouselDescriptionText?: string | null;
  carouselItems: any | null;
}

const useStyles = makeStyles(theme => ({
  container: {
    maxWidth: 1500,
    margin: '0 auto',
    textAlign: 'center',
    marginBottom: theme.spacing(4),
    marginTop: theme.spacing(4)
  },
  headerContainer: {
    textAlign: 'center',
    marginBottom: theme.spacing(2)
  },
  heading: {
    marginBottom: theme.spacing(1)
  },
  subHeading: {
    marginBottom: theme.spacing(4)
  },
  carouselContainer: {
    position: 'relative',
    overflow: 'hidden auto',
    width: '100%',
    scrollBehavior: 'smooth',
    '&::-webkit-scrollbar': {
      display: 'none'
    },
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    '& $arrowButton': {
      opacity: 0
    },
    '&:hover $arrowButton': {
      opacity: 1
    }
  },
  carouselTrack: {
    display: 'flex',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1)
  },
  arrowButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    transition: 'opacity 0.3s ease',
    border: 'none',
    backgroundColor: '#fff',
    boxShadow: '0 0 4px rgba(0,0,0,0.2)',
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    padding: 0,
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  },
  arrowLeft: {
    left: theme.spacing(1)
  },
  arrowRight: {
    right: theme.spacing(1)
  }
}));

const Carousel: FC<CarouselProps> = ({
  carouselHeader,
  carouselDescriptionText,
  carouselItems
}) => {
  const items = Array.isArray(carouselItems)
    ? carouselItems.filter(Boolean)
    : [];
  const classes = useStyles();
  const scrollRef = useRef<HTMLDivElement>(null);
  const itemWidth = 300;

  const scrollClick = (scrollLeft: boolean) => {
    if (scrollRef.current) {
      const currentScroll = scrollRef.current.scrollLeft;
      const cardToCenter = Math.round(currentScroll / itemWidth);
      const scrollTarget = cardToCenter * itemWidth;
      scrollRef.current.scrollTo({
        left: scrollLeft ? scrollTarget - itemWidth : scrollTarget + itemWidth,
        behavior: 'smooth'
      });
    }
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => scrollClick(false),
    onSwipedRight: () => scrollClick(true),
    delta: 40
  });

  return (
    <Box className={classes.container}>
      <Box className={classes.headerContainer}>
        {carouselHeader && (
          <H2 typoStyle="lg" mb="sm" className={classes.heading}>
            {carouselHeader}
          </H2>
        )}
        {carouselDescriptionText && (
          <Box mb="sm">{carouselDescriptionText}</Box>
        )}
      </Box>
      <Box style={{ position: 'relative' }}>
        <div className={classes.carouselContainer} ref={scrollRef}>
          <Box className={classes.carouselTrack} {...handlers}>
            {items.map((item, idx) => (
              <CarouselItem
                key={`carousel-item-${idx}`}
                carouselItemImage={item.carouselItemImage}
                carouselItemHeader={item.carouselItemHeader}
                carouselItemDescriptionText={item.carouselItemDescriptionText}
              />
            ))}
          </Box>
        </div>
        <IconButton
          onClick={() => scrollClick(true)}
          className={`${classes.arrowButton} ${classes.arrowLeft}`}
        >
          <Left />
        </IconButton>
        <IconButton
          onClick={() => scrollClick(false)}
          className={`${classes.arrowButton} ${classes.arrowRight}`}
        >
          <Right />
        </IconButton>
      </Box>
    </Box>
  );
};

export default Carousel;
